/**
 * 레슨 액션 관련 헬퍼 함수들
 * 최소 필드만 있으면 동작하도록 구현
 */

import { ticketLangTypeSchema } from '@server/schemas'
import { z } from 'zod'

import { CLASS_STATUS, CLASS_STATUS_KR_MAP } from '../../model/constants/card'
import type {
  LessonBookingFields,
  LessonBookingValidationFields,
  LessonCancelFields,
  LessonChangeAllowedFields,
  LessonEnterFields,
  LessonStatusFields,
} from '../../model/types/action'

const ALLOW_CHANGE_TIME = 60 * 60
const MAX_PRESTUDY_TIME = 8 // 8분
export const CLASSROOM_ENTRY_OPEN_BEFORE_START_TIME = 5 * 60 // 수업방 입장 전환 기준 (시작 5분 전, 초 단위)

/**
 * 예약 변경이 허용되지 않는지 확인
 */
export const isChangeNotAllowed = (fields: LessonChangeAllowedFields): boolean => {
  const allowTime = fields.allowChangeTime ?? ALLOW_CHANGE_TIME
  return fields.startDateTime - allowTime < fields.currentTime
}

/**
 * 예약이 가능한지 확인 (예습 8분 이상 완료)
 * @deprecated Use isBookingEnabled from card.ts instead (더 간단한 시그니처)
 */
export const isBookingEnabledForAction = (fields: LessonBookingFields): boolean => {
  return (fields.preStudyTime ?? 0) >= MAX_PRESTUDY_TIME
}

/**
 * 예습 진행률 계산 (0-100%)
 */
export const getPreStudyProgress = (preStudyTime: number | null): number => {
  return ((preStudyTime ?? 0) / MAX_PRESTUDY_TIME) * 100
}

/**
 * 체험 레슨 여부 확인 (여러 형식 지원)
 */
export const isTrialLesson = (value: boolean | string | null | undefined): boolean => {
  if (typeof value === 'boolean') {
    return value
  }
  return value === 'Y'
}

/**
 * 무제한 티켓 여부 확인 (여러 형식 지원)
 */
export const isUnlimitedTicket = (value: boolean | string | null | undefined): boolean => {
  if (typeof value === 'boolean') {
    return value
  }
  return value === 'Y'
}

/**
 * 예약 상태 확인
 */
export const isReserved = (fields: LessonStatusFields): boolean => {
  const classStatus = CLASS_STATUS_KR_MAP[fields.classStatus as keyof typeof CLASS_STATUS_KR_MAP]
  return classStatus === CLASS_STATUS.RESERVED && (fields.teacherId ?? 0) > 0
}

/**
 * 예약 가능 여부 검증
 */
export const validateBooking = (
  fields: LessonBookingValidationFields,
): { valid: boolean; error?: 'CANNOT_BOOKING' | 'NOT_ENOUGH_TICKET' | 'DUPLICATE_BOOKING' } => {
  // teacherId와 classId 확인
  if (!fields.teacherId || fields.teacherId === 0 || !fields.classId) {
    return { valid: false, error: 'CANNOT_BOOKING' }
  }

  // 티켓 수량 확인
  if (fields.remainTicketCount === 0) {
    return { valid: false, error: 'NOT_ENOUGH_TICKET' }
  }

  // 중복 예약 확인 (무제한 티켓)
  if (fields.isUnlimitedTicket && (fields.regClassCount ?? 0) > 0) {
    return { valid: false, error: 'DUPLICATE_BOOKING' }
  }

  return { valid: true }
}

/**
 * 예습인지 레슨인지 확인
 */
export const getClassroomEntryStartTime = (startDateTime: number): number => {
  return startDateTime - CLASSROOM_ENTRY_OPEN_BEFORE_START_TIME
}

/**
 * 예습인지 레슨인지 확인
 * - 수업 시작 5분 전까지는 예습방
 * - 수업 시작 5분 전부터는 수업방
 */
export const isPreStudyPhase = (startDateTime: number, currentTime: number): boolean => {
  return currentTime < getClassroomEntryStartTime(startDateTime)
}

/**
 * 입장 가능 여부 확인 (레슨 시작 5분 전부터)
 */
export const canEnterClassroom = (startDateTime: number, currentTime: number): boolean => {
  return currentTime >= getClassroomEntryStartTime(startDateTime)
}

/**
 * 언어 타입 정규화 ('EN' | 'JP' | 'KR' | 'ENJP' | undefined)
 * 실제 수업 언어(KR 포함)와 ENJP 수강권을 모두 허용한다.
 */
export const normalizeLangType = (
  langType: string | null | undefined,
): Undefinable<z.infer<typeof ticketLangTypeSchema>> => {
  if (!langType) {
    return undefined
  }

  const result = ticketLangTypeSchema.safeParse(langType)
  return result.success ? result.data : undefined
}

type CreatedClassIdFields = {
  class_id?: string | number | null
  classId?: string | number | null
}

export const getCreatedClassId = (fields: CreatedClassIdFields): string => {
  const classId = fields.class_id ?? fields.classId

  if (classId === null || classId === undefined || classId === '') {
    throw new Error('LESSON_CREATE_CLASS_ID_MISSING')
  }

  return String(classId)
}

/**
 * LessonCancelFields로 변환 (다양한 형식 지원)
 */
export const toLessonCancelFields = (data: {
  classId: number
  trial_yn?: 'Y' | 'N' | null
  unlimit_yn?: 'Y' | 'N' | null
  isTrialLesson?: boolean
  isUnlimitedTicket?: boolean
}): LessonCancelFields => {
  return {
    classId: data.classId,
    isTrialLesson: data.isTrialLesson ?? data.trial_yn === 'Y',
    isUnlimitedTicket: data.isUnlimitedTicket ?? data.unlimit_yn === 'Y',
  }
}

/**
 * LessonEnterFields로 변환
 */
export const toLessonEnterFields = (data: {
  class_course_id?: string | number | null
  class_level?: string | number | null
  class_week?: string | number | null
  lang_type?: string | null
  unix_class_start_datetime?: string | number | null
  classCourseId?: number
  classLevel?: number
  classWeek?: number
  langType?: string | null
  startDateTime?: number
}): LessonEnterFields => {
  return {
    classCourseId: data.classCourseId ?? Number(data.class_course_id ?? '0'),
    classLevel: data.classLevel ?? Number(data.class_level ?? '1'),
    classWeek: data.classWeek ?? Number(data.class_week ?? '1'),
    langType: data.langType ?? data.lang_type,
    startDateTime: data.startDateTime ?? Number(data.unix_class_start_datetime ?? '0'),
  }
}
